import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  readonly children: ReactNode;
}

interface State {
  readonly error: Error | null;
}

/**
 * Without this, a render error unmounts the whole tree and leaves a blank white
 * page with no explanation, which is indistinguishable from an empty board.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Only the message and component stack: never board contents.
    console.error('Slate Whiteboard crashed:', error.message, info.componentStack);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    return (
      <div className="boot">
        <div className="card" style={{ maxWidth: 520, textAlign: 'left' }}>
          <h1 style={{ fontSize: 17, marginTop: 0 }}>The editor hit an error and stopped</h1>
          <p>
            Your boards are safe in this browser&rsquo;s storage. Reloading reopens the last board
            you had open.
          </p>
          <pre
            style={{
              fontFamily: 'var(--mono)',
              fontSize: 12,
              whiteSpace: 'pre-wrap',
              background: 'var(--surface-2)',
              border: '1px solid var(--border)',
              borderRadius: 8,
              padding: '10px 12px',
              color: 'var(--danger)',
              overflowX: 'auto',
            }}
          >
            {error.message}
          </pre>
          <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
            <button type="button" className="btn btn-primary" onClick={() => window.location.reload()}>
              Reload
            </button>
            <button type="button" className="btn" onClick={() => this.setState({ error: null })}>
              Try to carry on
            </button>
          </div>
        </div>
      </div>
    );
  }
}
